import React, { useEffect, useState } from 'react';
import { Grid, Tooltip, TextField, Chip, Skeleton, Box, IconButton } from '@mui/material';
import './Task.css';
import TaskChip from './TaskChip';
import ExternalTaskChip from './ExternalTaskChip';
import { send_request } from '../../utils/Request';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { TipTapEditor } from '../TipTap';
import { format } from '../../utils/Date';
import { withStyles } from '@mui/styles';
import ChipInput from '../ChipInput/ChipInput';
import { Autocomplete } from '@mui/material';
import TextEditor from '../TextEditor/TextEditor';
import { ExpandMore, ExpandLess, Email, Save, Close } from '@mui/icons-material';
import { getComponentSessionWithSessionIdAndComponentId } from '../../utils/Requests/Projects';
import { generateTimeTracking } from './TaskUtil';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import WarningBox from '../pages/CommonStyledComponents/WarningBox';
import ExternalWarningBox from '../pages/CommonStyledComponents/ExternalWarningBox';
import TaskAssigneeChip from './TaskAssigneeChip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { getColourFromString } from '../../utils/ColourNameGenerator';
import { ListItemChip } from '../../utils/ListItemChip';
import {
  styles,
  StyledATag,
  StyledBox,
  StyledPaper,
  ExpandButton,
  SectionContainer,
  SectionTitle,
  ContentParagraph,
  UserSection,
  UserChip,
  InfoCard,
  InfoItem,
  InfoLabel,
  InfoValue,
  TimeTrackingContainer,
  TimeUnit,
  TimeValue,
  TimeLabel,
  TaskIdContainer,
  ComponentNameParagraph,
  DescriptionContainer,
  PulsingIndicator,
  LiveTimeContainer,
  TaskSubjectHeader,
  NoInformationMessage,
  AppBarButton,
  FormTextField,
  EditorContainer,
  AssignedOnSpan,
} from './styles/TaskDetailsStyle';

dayjs.extend(duration);
dayjs.extend(relativeTime);

// TipTapEditor used for read-only task description display
const excludeDisabledUsers = users => {
  if (!Array.isArray(users)) {
    return [];
  }

  return users.filter(user => user?.roleDisabled !== true);
};

const TaskDetails = props => {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignedEmail, setAssignedEmail] = useState([]);
  const [taskSubject, setTaskSubject] = useState('');
  const [assignedUser, setAssignedUser] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [taskDescription, setTaskDescription] = useState('');
  const [expandDetails, setExpandDetails] = useState(false);
  const [timeTracking, setTimeTracking] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const { expanded, reloadPanel, classes, appBarButtons, editMode, onEditComplete, onEditCancel } =
    props;

  useEffect(() => {
    if (initialized) return;
    initializeTaskDetails();
  }, [props.task, reloadPanel, initialized]);

  useEffect(() => {
    if (initialized) return;
    refreshTaskDetails();
  }, [props.id, props.task, reloadPanel, initialized]);

  useEffect(() => {
    if (task && props.task && task.taskId !== props.task.taskId) {
      setTask(props.task);
      setAssignedEmail(props.task.assignedEmail || []);
      setTaskSubject(props.task.taskSubject || '');
      setTaskDescription(props.task.taskDescription || '');
      setInitialized(false);
    }
  }, [props.task?.taskId]);

  useEffect(() => {
    let interval = null;

    if (task && task.status === 'TODO' && task.assignedOn) {
      const startTime = dayjs(task.assignedOn);
      setElapsedTime(dayjs.duration(dayjs().diff(startTime)));

      interval = setInterval(() => {
        const startTime = dayjs(task.assignedOn);
        setElapsedTime(dayjs.duration(dayjs().diff(startTime)));
      }, 1000);
    } else {
      setElapsedTime(null);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [task]);

  useEffect(() => {
    fetchUsersForEdit();
  }, [editMode]);

  useEffect(() => {
    const updatedAssignedUsers =
      Array.isArray(task?.assignedTo)
        ? task.assignedTo
            .map(
              username =>
                allUsers.find(user => user.userName === username) || {
                  userName: username,
                  displayName: username,
                }
            )
            .filter(Boolean)
        : [];

    setAssignedUser(updatedAssignedUsers);
  }, [task?.assignedTo, allUsers]);

  const saveTaskDetails = () => {
    if (!task) return;

    setLoading(true);

    const updatedTask = { ...task };
    updatedTask.overDue = false;

    if (updatedTask.dueDate) {
      const nowDate = new Date();
      const nowDataMillis = nowDate.getTime();

      const dueDate = new Date(updatedTask.dueDate);
      const dueDateMillis = dueDate.getTime();

      if (nowDataMillis > dueDateMillis) {
        updatedTask.overDue = true;
      }
    }

    updatedTask.taskSubject = taskSubject;
    updatedTask.assignedEmail = assignedEmail;
    updatedTask.assignedTo = [];

    assignedUser.forEach(user => {
      updatedTask.assignedTo.push(user.userName);
    });

    updatedTask.taskDescription = taskDescription;

    send_request('task/task/update_task_detail', updatedTask, null, 'POST').then(() => {
      setLoading(false);
      setTask(updatedTask);
      onEditComplete();
    });
  };

  const cancelEdit = () => {
    if (!task) return;

    setAssignedEmail(task.assignedEmail || []);
    setTaskSubject(task.taskSubject || '');
    setTaskDescription(task.taskDescription || '');
    onEditCancel();
  };

  const handleChangeEmail = chip => {
    let key = chip.key;
    if (chip.isNotPlaceholder !== true) {
      key = '${' + key + '}';
    }
    const updatedEmail = [...assignedEmail, key];
    setAssignedEmail(updatedEmail);
  };

  const handleDeleteEmail = index => {
    const updatedEmails = assignedEmail.filter((_, i) => i !== index);
    setAssignedEmail(updatedEmails);
  };

  const handleSetUsers = (event, inputValue, reason) => {
    if (reason === 'clear') {
      setAssignedUser([]);
    } else {
      setAssignedUser(inputValue);
    }
  };

  const handleChangeDescription = html => {
    setTaskDescription(html);
  };

  const handleTaskDueDateChange = value => {
    if (!task) return;
    const newTask = { ...task };
    newTask.dueDate = value ? value.toISOString() : null;
    setTask(newTask);
  };

  const fetchUsersForEdit = async () => {
    if (editMode) {
      const usersResponse = await send_request('authz-service/get-active-users', '', '', 'get');
      const allUsersList = excludeDisabledUsers(usersResponse?.data);
      setAllUsers(allUsersList);
    } else {
      setAllUsers([]);
    }
  };

  const updateWorkflowNameInTask = async (componentIds, fetchedTask) => {
    const taskInfoResponse = await send_request(
      'project-service/project/get_project_info',
      componentIds,
      '',
      'POST'
    );

    const taskExtras = taskInfoResponse.data;
    if (fetchedTask.componentId === taskExtras[0]?.componentId) {
      fetchedTask.componentName = taskExtras[0].componentName;
      fetchedTask.projectName = taskExtras[0].projectName;
      fetchedTask.projectId = taskExtras[0].projectId;
    }
  };

  const updateTaskTimeTracking = async fetchedTask => {
    const res = await getComponentSessionWithSessionIdAndComponentId(
      fetchedTask.sessionId,
      fetchedTask.componentId
    );

    const componentSession = res.data;
    if (componentSession) {
      const componentTimeTracking = generateTimeTracking(componentSession);
      setTimeTracking(componentTimeTracking);
    }
  };

  const updateTaskOverdue = fetchedTask => {
    fetchedTask.overDue = false;
    if (fetchedTask.dueDate) {
      const nowDate = new Date();
      const nowDataMillis = nowDate.getTime();

      const dueDate = new Date(fetchedTask.dueDate);
      const dueDateMillis = dueDate.getTime();

      if (nowDataMillis > dueDateMillis) {
        fetchedTask.overDue = true;
      }
    }
  };

  const initializeTaskDetails = async () => {
    const { task: taskFromParent } = props;

    if (taskFromParent && !reloadPanel) {
      setTask(taskFromParent);
      setAssignedEmail(taskFromParent.assignedEmail || []);
      setTaskSubject(taskFromParent.taskSubject || '');
      setLoading(false);

      try {
        await updateTaskTimeTracking(taskFromParent);
      } catch (error) {
        console.error('Error fetching component session:', error);
      }

      setInitialized(true);
    }
  };

  const refreshTaskDetails = async () => {
    const { id } = props;

    if (!id) {
      setInitialized(true);
      return;
    }

    try {
      const response = await send_request(`task/task/get_task/${id}`);
      let fetchedTask = response.data;
      await updateTaskTimeTracking(fetchedTask);

      let componentIds = [];
      componentIds.push(fetchedTask.componentId);
      await updateWorkflowNameInTask(componentIds, fetchedTask);
      updateTaskOverdue(fetchedTask);

      setTask(fetchedTask);
      setAssignedEmail(fetchedTask.assignedEmail || []);
      setTaskSubject(fetchedTask.taskSubject || '');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching task:', err);
      setLoading(false);
    }

    setInitialized(true);
  };

  const renderWorkflowName = task => {
    const { projectName, projectId } = task;

    if (!projectId) {
      return 'Deleted workflow';
    }

    if (projectName) {
      if (projectName.length === 0) {
        return projectId;
      } else {
        return projectName;
      }
    } else {
      return projectId;
    }
  };

  const renderTimeTracking = timeTracking => {
    if (task?.status === 'TODO' && task.assignedOn && elapsedTime) {
      return (
        <TimeTrackingContainer>
          <LiveTimeContainer>
            <PulsingIndicator />
          </LiveTimeContainer>
          {elapsedTime.asDays().toFixed() > 0 && (
            <TimeUnit>
              <TimeValue>{elapsedTime.asDays().toFixed()}</TimeValue>
              <TimeLabel>days</TimeLabel>
            </TimeUnit>
          )}
          {elapsedTime.hours() > 0 && (
            <TimeUnit>
              <TimeValue>{elapsedTime.hours()}</TimeValue>
              <TimeLabel>hours</TimeLabel>
            </TimeUnit>
          )}
          {elapsedTime.minutes() > 0 && (
            <TimeUnit>
              <TimeValue>{elapsedTime.minutes()}</TimeValue>
              <TimeLabel>min</TimeLabel>
            </TimeUnit>
          )}
          <TimeUnit>
            <TimeValue>{elapsedTime.seconds()}</TimeValue>
            <TimeLabel>sec</TimeLabel>
          </TimeUnit>
        </TimeTrackingContainer>
      );
    }

    let timeTrackingInDuration = dayjs.duration(timeTracking);

    return (
      <TimeTrackingContainer>
        {timeTrackingInDuration.asDays().toFixed() > 0 && (
          <TimeUnit>
            <TimeValue>{timeTrackingInDuration.asDays().toFixed()}</TimeValue>
            <TimeLabel>days</TimeLabel>
          </TimeUnit>
        )}
        {timeTrackingInDuration.hours() > 0 && (
          <TimeUnit>
            <TimeValue>{timeTrackingInDuration.hours()}</TimeValue>
            <TimeLabel>hours</TimeLabel>
          </TimeUnit>
        )}
        {timeTrackingInDuration.minutes() > 0 && (
          <TimeUnit>
            <TimeValue>{timeTrackingInDuration.minutes()}</TimeValue>
            <TimeLabel>min</TimeLabel>
          </TimeUnit>
        )}
        {timeTrackingInDuration.seconds() > 0 && (
          <TimeUnit>
            <TimeValue>{timeTrackingInDuration.seconds()}</TimeValue>
            <TimeLabel>sec</TimeLabel>
          </TimeUnit>
        )}
      </TimeTrackingContainer>
    );
  };

  if (task != null && !loading) {
    const isTaskUnassigned = task?.assignedTo?.length === 0 && task?.assignedEmail?.length === 0;
    return (
      <Box className={classes.root}>
        {!expanded && (
          <Box
            className="appBarButtons"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: editMode ? 'flex-end' : 'flex-start',
            }}
          >
            {editMode ? (
              <>
                <AppBarButton
                  startIcon={<Close />}
                  onClick={cancelEdit}
                  sx={{ marginRight: '8px' }}
                >
                  Cancel
                </AppBarButton>
                <AppBarButton startIcon={<Save />} onClick={saveTaskDetails}>
                  Save Edits
                </AppBarButton>
              </>
            ) : (
              <>{appBarButtons}</>
            )}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <TaskChip overDue={task.overDue} value={task.status} extras={task} />
          <TaskSubjectHeader>
            {task.isExternal && <ExternalTaskChip />} {task.taskSubject}
          </TaskSubjectHeader>
        </Box>
        {expanded && (
          <Box
            className="appBarButtons"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: '1rem',
              justifyContent: editMode ? 'flex-end' : 'flex-start',
            }}
          >
            {editMode ? (
              <>
                <AppBarButton
                  startIcon={<Close />}
                  onClick={cancelEdit}
                  sx={{ marginRight: '8px' }}
                >
                  Cancel
                </AppBarButton>
                <AppBarButton startIcon={<Save />} onClick={saveTaskDetails}>
                  Save Edits
                </AppBarButton>
              </>
            ) : (
              <>{appBarButtons}</>
            )}
          </Box>
        )}
        <StyledPaper>
          <Grid container item direction={'column'} xs={12}>
            <StyledBox>
              {!task.isExternal && (
                <StyledATag href={`/project/canvas/${task.projectId}`} target="_blank">
                  {renderWorkflowName(task)}
                </StyledATag>
              )}
              <AssignedOnSpan>
                {format(task.assignedOn, useProjectStore.getState().timezone, 'timeline')}
              </AssignedOnSpan>
            </StyledBox>
            {task.isExternal && (
              <>
                <Grid container marginTop={2}>
                  <Grid item xs={12}>
                    <ExternalWarningBox
                      width="full-width"
                      vertical="flex-start"
                      horizontal="flex-start"
                      text={
                        <Grid container rowSpacing={1}>
                          <Grid item xs={12}>
                            <b>This task</b> was assigned to you from an external account
                          </Grid>
                        </Grid>
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container marginBottom={2} marginTop={2}>
                  <Grid item xs={12}>
                    <SectionTitle>From</SectionTitle>
                  </Grid>
                  <Grid item xs={12}>
                    {task.externalSender ? (
                      <UserChip>
                        <Email fontSize="small" className={'margin-right'} />
                        {task.externalSender}
                      </UserChip>
                    ) : (
                      <NoInformationMessage>No sender information</NoInformationMessage>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
            <SectionContainer>
              <SectionTitle>Assigned</SectionTitle>
              <UserSection>
                {editMode === false ? (
                  <>
                    {task.assignedTo &&
                      task.assignedTo.length > 0 &&
                      task.assignedTo.map((assigned, id) => (
                        <TaskAssigneeChip
                          key={id}
                          user={assigned}
                          userType="USER"
                          isExternal={task.isExternal}
                        />
                      ))}
                    {task.assignedEmail &&
                      task.assignedEmail.length > 0 &&
                      task.assignedEmail.map((assigned, id) => (
                        <TaskAssigneeChip
                          key={id}
                          user={assigned}
                          userType="EMAIL"
                          isExternal={task.isExternal}
                        />
                      ))}
                    {isTaskUnassigned && <NoInformationMessage>No assignees</NoInformationMessage>}
                  </>
                ) : (
                  ''
                )}
              </UserSection>
              {editMode === true && (
                <>
                  <Autocomplete
                    options={allUsers}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        let item = option.displayName;
                        return (
                          <ListItemChip
                            {...getTagProps({ index })}
                            label={item}
                            colour={getColourFromString(item)}
                          />
                        );
                      })
                    }
                    fullWidth
                    size={'small'}
                    multiple
                    freeSolo={false}
                    openOnFocus={true}
                    getOptionLabel={option => {
                      return option.displayName;
                    }}
                    variant={'outlined'}
                    value={assignedUser}
                    onChange={(event, inputValue, reason) =>
                      handleSetUsers(event, inputValue, reason)
                    }
                    renderInput={params => {
                      return (
                        <TextField
                          {...params}
                          placeholder={'Select users'}
                          size={'small'}
                          variant="outlined"
                          fullWidth
                        />
                      );
                    }}
                  />
                </>
              )}
            </SectionContainer>
            <Grid container item xs={12} direction={'row'} className={'padding-bottom-two'}>
              {editMode === true && (
                <>
                  <ChipInput
                    inputValue={assignedEmail}
                    fullWidth={true}
                    placeholder={'Insert emails '}
                    onPlaceholderSelected={handleChangeEmail}
                    onDelete={index => {
                      handleDeleteEmail(index);
                    }}
                  />
                </>
              )}
            </Grid>
            {!expandDetails && (
              <Tooltip title="Expand details" placement="top">
                <ExpandButton onClick={() => setExpandDetails(true)}>
                  <ExpandMore />
                </ExpandButton>
              </Tooltip>
            )}
            {expandDetails && (
              <>
                <SectionTitle>Task subject</SectionTitle>
                {editMode == false && <ContentParagraph>{taskSubject}</ContentParagraph>}
                {editMode == true && (
                  <>
                    <FormTextField
                      variant="outlined"
                      value={taskSubject}
                      onChange={event => {
                        setTaskSubject(event.target.value);
                      }}
                      fullWidth
                      className={'padding-bottom-two'}
                    />
                  </>
                )}

                <SectionTitle>Description</SectionTitle>
                {editMode === false &&
                task.taskDescription != null &&
                task.taskDescription.length !== 0 ? (
                  <DescriptionContainer>
                    <TipTapEditor
                      className={'taskDescription'}
                      value={task.taskDescription}
                      readOnly={true}
                      showToolbar={false}
                    />
                  </DescriptionContainer>
                ) : (
                  <ContentParagraph>-</ContentParagraph>
                )}
                {editMode === true && (
                  <EditorContainer className={'padding-bottom-two'}>
                    <TextEditor
                      editorFocus={() => {}}
                      onChange={handleChangeDescription}
                      disableTooltip
                      contentStyles={{
                        border: '1px solid rgba(0, 0, 0, 0.16)',
                        boxShadow: 'none',
                        padding: '8px',
                        minWidth: '100%',
                      }}
                      html={taskDescription}
                    />
                  </EditorContainer>
                )}

                {!task.isExternal && (
                  <>
                    <SectionTitle>Component Name</SectionTitle>
                    <ComponentNameParagraph>
                      {task.componentName ? task.componentName : '-'}
                    </ComponentNameParagraph>
                  </>
                )}
                <InfoCard>
                  <InfoItem>
                    <InfoLabel>Due Date</InfoLabel>
                    <InfoValue
                      className={task.overDue && task.status !== 'DONE' ? 'txt-overdue' : '-'}
                    >
                      {editMode ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            value={task.dueDate ? dayjs(task.dueDate) : null}
                            onChange={handleTaskDueDateChange}
                            slotProps={{
                              textField: {
                                size: 'small',
                              },
                            }}
                          />
                        </LocalizationProvider>
                      ) : task.dueDate == null ? (
                        '-'
                      ) : (
                        format(task.dueDate, useProjectStore.getState().timezone) +
                        ' (Due ' +
                        format(task.dueDate, useProjectStore.getState().timezone, 'relative') +
                        ')'
                      )}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Date Assigned</InfoLabel>
                    <InfoValue>
                      {task.assignedOn == null
                        ? '-'
                        : format(task.assignedOn, useProjectStore.getState().timezone, 'relative')}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Date Completed</InfoLabel>
                    <InfoValue>
                      {task.completedDate == null
                        ? 'NA'
                        : format(task.completedDate, useProjectStore.getState().timezone, 'relative')}
                    </InfoValue>
                  </InfoItem>
                </InfoCard>
                <SectionContainer>
                  <SectionTitle>
                    {task.status === 'TODO' ? 'Time elapsed' : 'Time to complete'}
                  </SectionTitle>
                  {timeTracking ? (
                    renderTimeTracking(timeTracking.timeTracking)
                  ) : (
                    <ContentParagraph>-</ContentParagraph>
                  )}
                </SectionContainer>

                <SectionContainer>
                  <SectionTitle>Task ID</SectionTitle>
                  <TaskIdContainer>{task.taskId}</TaskIdContainer>
                </SectionContainer>

                <Tooltip title="Collapse details" placement="top">
                  <ExpandButton onClick={() => setExpandDetails(false)}>
                    <ExpandLess />
                  </ExpandButton>
                </Tooltip>
              </>
            )}
          </Grid>
        </StyledPaper>
      </Box>
    );
  } else
    return (
      <>
        <Skeleton width="100%" height={'5vh'} />
        <Skeleton width="100%" height={'10vh'} />
      </>
    );
};
export default withStyles(styles)(TaskDetails);
